import { useEffect, useState } from 'react'
import { Button, Input, MessageBar, Subtitle2, Divider } from '@fluentui/react-components'
import { apiFetchJson } from '../lib/apiFetch'
import { useMe } from '../lib/me'
import { SERVICE_INGESTION, SERVICE_LABELS, TIMER_ONLY_SERVICES } from '../lib/services'
import CsvImportControl from './CsvImportControl'
import ClearServiceDataButton from './ClearServiceDataButton'
import ServiceIconPicker from './ServiceIconPicker'
import LicenseTypesTab from './LicenseTypesTab'
import type { ServiceConfigResponse } from '../types'

interface SyncResult {
  run: { recordsProcessed: number; errorsJson: string }
}

const DEFAULT_STALE_LOGIN_DAYS = 90

/** The "stale login" alert threshold — inherently a judgment call per service, only meaningful
 * for a module reporting lastLoginAt (currently zoom). The icon itself is a separate, reusable
 * concern — see ServiceIconPicker, rendered alongside this in ServiceSettingsTab below. */
function StaleLoginDaysField(props: { serviceId: string }) {
  const { serviceId } = props
  const [staleLoginDays, setStaleLoginDays] = useState<number | null | undefined>(undefined)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    setLoaded(false)
    apiFetchJson<ServiceConfigResponse>('/api/service-config')
      .then(res => setStaleLoginDays(res.config[serviceId]?.staleLoginDays))
      .finally(() => setLoaded(true))
  }, [serviceId])

  function handleStaleDaysCommit(raw: string) {
    const next = raw === '' || Number.isNaN(Number(raw)) ? null : Number(raw)
    setStaleLoginDays(next)
    apiFetchJson('/api/service-config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ serviceId, staleLoginDays: next }),
    })
  }

  return (
    <div>
      <div style={{ marginBottom: 4, fontWeight: 600 }}>Stale login alert (days)</div>
      <Input
        disabled={!loaded}
        type="number"
        placeholder={String(DEFAULT_STALE_LOGIN_DAYS)}
        value={staleLoginDays != null ? String(staleLoginDays) : ''}
        onChange={(_e, d) => setStaleLoginDays(d.value === '' ? null : Number(d.value))}
        onBlur={e => handleStaleDaysCommit(e.target.value)}
        style={{ minWidth: 110 }}
      />
    </div>
  )
}

/** Sync controls (CSV import / "Sync now" / timer-only note) + the "Clear all data" reset —
 * everything that mutates this service's data, grouped under its own Settings tab so the
 * default Licenses tab (see ServiceLicensesTab.tsx) can be a read-only landing view. */
export default function ServiceSettingsTab(props: { serviceId: string; refreshKey: number; onChanged: () => void }) {
  const { serviceId, refreshKey, onChanged } = props
  const me = useMe()
  const ingestion = SERVICE_INGESTION[serviceId]
  const isTimerOnly = TIMER_ONLY_SERVICES.has(serviceId)
  const [syncing, setSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handleSync() {
    setSyncing(true)
    setSyncMessage(null)
    setError(null)
    try {
      const res = await apiFetchJson<SyncResult>(`/api/sync/${serviceId}`, { method: 'POST' })
      const { recordsProcessed, errorsJson } = res.run
      const errorCount = (JSON.parse(errorsJson) as string[]).length
      setSyncMessage(
        `Synced ${recordsProcessed} record${recordsProcessed === 1 ? '' : 's'}` +
          (errorCount ? `, ${errorCount} error${errorCount === 1 ? '' : 's'}` : '.'),
      )
      onChanged()
    } catch (e) {
      setError(String(e))
    } finally {
      setSyncing(false)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
        <ServiceIconPicker serviceId={serviceId} />
        <StaleLoginDaysField serviceId={serviceId} />
      </div>

      {ingestion === 'csv' ? (
        <CsvImportControl serviceId={serviceId} onApplied={onChanged} />
      ) : (
        <>
          {isTimerOnly ? (
            <MessageBar intent="info">
              {SERVICE_LABELS[serviceId] ?? serviceId} syncs automatically once a day. Manual
              sync isn't available here because its credentials live in Key Vault, which the
              API (Static Web App Free tier) has no access to — see docs/ARCHITECTURE.md.
            </MessageBar>
          ) : (
            <Button appearance="primary" onClick={handleSync} disabled={syncing}>
              {syncing ? 'Syncing...' : 'Sync now'}
            </Button>
          )}
          {syncMessage && (
            <MessageBar intent="success" style={{ marginTop: 16 }}>
              {syncMessage}
            </MessageBar>
          )}
          {error && (
            <MessageBar intent="error" style={{ marginTop: 16 }}>
              Error: {error}
            </MessageBar>
          )}
        </>
      )}

      <Divider style={{ marginTop: 24, marginBottom: 16 }} />
      <Subtitle2 style={{ display: 'block', marginBottom: 12 }}>License types</Subtitle2>
      <LicenseTypesTab serviceId={serviceId} refreshKey={refreshKey} />

      {me.isAdmin && (
        <>
          <Divider style={{ marginTop: 24, marginBottom: 16 }} />
          <ClearServiceDataButton serviceId={serviceId} onCleared={onChanged} />
        </>
      )}
    </div>
  )
}
